import { useCallback, useEffect, useRef } from 'react';

import { usePathname } from 'expo-router';
import { ICartEntry } from 'features/cart/types';

import { routes } from 'navigation/routing';

import { useCartIncentivesEvaluation } from './use-cart-incentives-evaluation';

const DEFAULT_VALIDATION_DEBOUNCE_MS = 250;

type UseCartLiveValidationParams = {
  cartEntries: ICartEntry[];
};

/**
 * Centralizes cart incentive re-validation for cart route interactions.
 * It intentionally avoids broad dependency-driven effects by exposing
 * explicit trigger functions for entry and mutation events.
 */
export const useCartLiveValidation = ({ cartEntries }: UseCartLiveValidationParams) => {
  const pathname = usePathname();
  const { evaluateCartIncentives } = useCartIncentivesEvaluation({ cartEntries });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isCartRoute = pathname?.startsWith(routes.cart);

  const clearPendingValidation = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  const validateCartNow = useCallback(() => {
    if (!isCartRoute) {
      return;
    }
    clearPendingValidation();
    evaluateCartIncentives();
  }, [clearPendingValidation, evaluateCartIncentives, isCartRoute]);

  const requestCartValidation = useCallback(
    (delayMs = DEFAULT_VALIDATION_DEBOUNCE_MS) => {
      if (!isCartRoute) {
        return;
      }
      clearPendingValidation();
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        evaluateCartIncentives();
      }, delayMs);
    },
    [clearPendingValidation, evaluateCartIncentives, isCartRoute]
  );

  // Only used to avoid pending timers after unmount.
  useEffect(() => clearPendingValidation, [clearPendingValidation]);

  return {
    validateCartNow,
    requestCartValidation,
  } as const;
};
